import { useLocation, useParams } from "wouter";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import type { RuleForm } from "@/shared/partnerschema";
import { AlertDialog } from "@/widgets/AlertDailog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    useGetAttributes,
    useGetAttributes2,
    useGetDataTypes,
    useGetRuleCompose,
    useCreateRuleValue,
    useUpdateRuleValue,
} from "@/hooks/useLocalStorage";
import { ExpressionBuilderModal } from "./ExpressionBuilderModal";
import RuleGenerate from "./RuleGenerate";
import ModifyPricing from "./ModifyPricing";
import { PricingRow, RuleFormData } from "./Updatebuilderschema";
import { buildFinalRule, buildPricingString } from "./RuleBuilderFunction";


// ─── Constants ────────────────────────────────────────────────────────────────

const ruleTypeOptions = [
    { value: "0", label: "Eligibility Rules" },
    { value: "1", label: "Pricing Rules" },
    { value: "2", label: "Credit Rating Rules" },
    { value: "4", label: "Workflow Entry Conditions" },
    { value: "5", label: "Compliance Rules" },
    { value: "6", label: "Form Rules" },
];

const getRuleTypeLabel = (typeValue: string) =>
    ruleTypeOptions.find((opt) => opt.value === typeValue)?.label ?? "Unknown";

// ─── Component ────────────────────────────────────────────────────────────────

export default function ModifyValue() {
    // wouter doesn't support typed params — cast via any
    const params = useParams() as any;
    const ruleId: string = params.ruleId ?? "";

    const [location, navigate] = useLocation();

    // State passed from RulesSetup via navigate
    const locationState = (window.history.state as any)?.state ?? {};
    const ruleObj: RuleForm | undefined = locationState?.object;
    const offerObj: any = locationState?.offerData;

    const methods = useForm<RuleFormData>({
        defaultValues: {
            ruleType: { value: "-1", label: "" },
            ruleExpression: "",
            rows: [{ id: 1, isEditing: false, isNew: false, condition: "If" }],
            NestedRule: {
                groups: [
                    {
                        id: "group-1",
                        type: "group",
                        conditions: [
                            {
                                id: "cond-1",
                                dataType: "",
                                attribute: "",
                                operator: "==",
                                valueType: "operand",
                                dataType2: "",
                                attribute2: "",
                            },
                        ],
                        logicalOperators: [],
                    },
                ],
                groupOperators: [],
            },
            NestedRuleString: "",
            pricingRows: [
                {
                    id: 1,
                    isEditing: false,
                    dataType: "",
                    attribute: "",
                    operator: "=",
                    dataType2: "",
                    attribute2: "",
                    expressionType: "operand",
                    expressionValue: "",
                    ExpressionJson: "",
                    isNew: false,
                },
            ],
            finalRule: "",
            isValidated: false,
        },
    });

    const { watch, setValue, register } = methods;
    const NestedRuleStringDa = watch("NestedRuleString");
    const pricingRows = watch("pricingRows");
    const ruleType = watch("ruleType");
    const finalRule = watch("finalRule");
    const remarks = watch("remarks") ?? "";

    const [attrCode, setAttrCode] = useState("");
    const [attrCode2, setAttrCode2] = useState("");
    const [showPricingAdjustment, setShowPricingAdjustment] = useState(false);
    const [openExpression, setOpenExpression] = useState(false);
    const [expressionData, setExpressionData] = useState<any>(null);
    const [ruleObjValue, setRuleObjValue] = useState<RuleForm | undefined>(ruleObj);
    const [alertmessage, setAlertmessage] = useState("");

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertStatus, setAlertStatus] = useState(false);
    const [alertMsg, setAlertMsg] = useState("");

    // ── TanStack hooks ────────────────────────────────────────────────────────
    const { data: dataTypesData } = useGetDataTypes();
    const dataType = dataTypesData?.items ?? [];

    const { data: attributesData } = useGetAttributes(attrCode);
    const attribute = attributesData?.items ?? [];

    const { data: attributesData2 } = useGetAttributes2(attrCode2);
    const attribute2 = attributesData2?.items ?? [];

    const { data: ruleBindVal } = useGetRuleCompose(ruleId);

    const createVersionMutation = useCreateRuleValue();
    const editVersionMutation = useUpdateRuleValue();

    // ── Effects ───────────────────────────────────────────────────────────────

    useEffect(() => {
        setRuleObjValue(ruleObj);
    }, [ruleObj]);

    // Populate form when rule compose data arrives
    useEffect(() => {
        if (!ruleBindVal) return;

        if (ruleBindVal.RuleType !== undefined) {
            setValue("ruleType", {
                value: String(ruleBindVal.RuleType),
                label: getRuleTypeLabel(String(ruleBindVal.RuleType)),
            });
        }

        if (ruleBindVal.RawRuleJson !== undefined) {
            setValue("NestedRule", ruleBindVal.RawRuleJson);
        }

        if (ruleBindVal.pricing && ruleBindVal.pricing.length > 0) {
            const mappedPricingRows: PricingRow[] = ruleBindVal.pricing.map(
                (pricing: any, index: number) => ({
                    id: index + 1,
                    isEditing: false,
                    dataType: pricing.Data_Type || "",
                    attribute: pricing.Attribute_Name || "",
                    operator: pricing.Operator?.trim() || "=",
                    dataType2: pricing.Exp_DataType || "",
                    attribute2: pricing.Exp_Attribute || "",
                    expressionType: pricing.Exp_Value ? "value" : "operand",
                    expressionValue: pricing.Exp_Value || "",
                    ExpressionJson: pricing.ExpressionJson || "",
                    isNew: false,
                })
            );
            setValue("pricingRows", mappedPricingRows);
            setShowPricingAdjustment(true);
        } else {
            setValue("pricingRows", [
                {
                    id: 1,
                    isEditing: false,
                    dataType: "",
                    attribute: "",
                    operator: "=",
                    dataType2: "",
                    attribute2: "",
                    expressionType: "operand",
                    expressionValue: "",
                    ExpressionJson: "",
                    isNew: false,
                },
            ]);
            setShowPricingAdjustment(false);
        }
    }, [ruleBindVal]);

    // Rebuild final rule whenever nested rule string or pricing rows change
    useEffect(() => {
        const completedPricingRows = pricingRows.filter((row: PricingRow) => !row.isEditing);
        const pricingString = buildPricingString(completedPricingRows);
        const finalRuleString = buildFinalRule(NestedRuleStringDa, pricingString);
        setValue("finalRule", `If${finalRuleString}`);
    }, [NestedRuleStringDa, pricingRows]);

    // Clear inline remarks error when user types
    useEffect(() => {
        setAlertmessage("");
    }, [remarks]);

    // Create version mutation result
    useEffect(() => {
        if (createVersionMutation.isSuccess) {
            setAlertStatus(true);
            setAlertMsg("New version created successfully");
            setAlertOpen(true);
            createVersionMutation.reset();
        } else if (createVersionMutation.isError) {
            const raw = createVersionMutation.error as Error;
            setAlertStatus(false);
            setAlertMsg(raw.message || "Failed to create version");
            setAlertOpen(true);
            createVersionMutation.reset();
        }
    }, [createVersionMutation.isSuccess, createVersionMutation.isError]);

    // Edit version mutation result
    useEffect(() => {
        if (editVersionMutation.isSuccess) {
            setAlertStatus(true);
            setAlertMsg("Version updated successfully");
            setAlertOpen(true);
            editVersionMutation.reset();
        } else if (editVersionMutation.isError) {
            const raw = editVersionMutation.error as Error;
            setAlertStatus(false);
            setAlertMsg(raw.message || "Failed to update version");
            setAlertOpen(true);
            editVersionMutation.reset();
        }
    }, [editVersionMutation.isSuccess, editVersionMutation.isError]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const buildApiPayload = () => ({
        Rule_Id: parseInt(ruleId ?? "0"),
        Data: finalRule,
        ObjectId: ruleObjValue?.ObjectId ?? null,
        Remarks: remarks,
        OfferId: offerObj?.Id,
    });

    const handleUpdate = () => {
        setAlertmessage("");
        if (!remarks) { setAlertmessage("Please Enter Remarks"); return; }
        editVersionMutation.mutate({
            objverid: String(ruleObjValue?.ObjectVersionId ?? ""),
            data: buildApiPayload() as any,
        });
    };

    const handleSaveNewVersion = () => {
        setAlertmessage("");
        if (!remarks) { setAlertmessage("Please Enter Remarks"); return; }
        createVersionMutation.mutate(buildApiPayload() as any);
    };

    const handleAlertClose = (open: boolean) => {
        if (!open) {
            setAlertOpen(false);
            // Navigate back on success
            if (alertStatus) navigate("~-1" as any);
        }
    };

    const callExpressionValue = (data: PricingRow) => {
        setExpressionData(data);
    };

    const isCreateLoading = createVersionMutation.isPending;
    const isEditLoading = editVersionMutation.isPending;

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <FormProvider {...methods}>
            <div className="p-6 space-y-3">
                <h1 className="text-xl font-semibold">Partner Rule Value Setup</h1>

                {/* Header bar */}
                <div className="flex items-center justify-between border-b pb-2">
                    <div className="flex">
                        <h2 className="text-l font-semibold mr-2">Rule:</h2>
                        <h2>{ruleId} ({ruleObjValue?.Name})</h2>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate("/exchangeoffers" as any)}
                        className="flex items-center gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </Button>
                </div>

                {/* Raw rule display */}
                <div className="border bg-white border-gray-200 rounded-lg p-2">
                    <Label className="font-medium text-gray-700">Raw Rule</Label>
                    <Textarea
                        value={ruleObjValue?.raw_rule ?? ""}
                        className="w-full bg-gray-100 text-gray-800 disabled:opacity-100 mt-1"
                        disabled
                        rows={5}
                    />
                </div>

                {/* Rule builder — hidden for Form Rules */}
                {ruleType.label !== "Form Rules" && (
                    <RuleGenerate
                        dataType={dataType}
                        attribute={attribute}
                        setAttrCode={setAttrCode}
                        version={true}
                    />
                )}

                {/* Pricing / Form Rules section */}
                {(ruleType.label === "Pricing Rules" || ruleType.label === "Form Rules") && (
                    <div className="pt-6">
                        <ModifyPricing
                            dataType={dataType}
                            attribute={attribute}
                            setAttrCode={setAttrCode}
                            openExpression={openExpression}
                            setOpenExpression={setOpenExpression}
                            setExpressionData={callExpressionValue}
                        />
                    </div>
                )}

                {/* Remarks */}
                <div className="flex flex-col p-2">
                    <Label className="font-medium text-gray-700 mb-2">
                        Remarks <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                        {...register("remarks")}
                        placeholder="Enter remarks"
                        className="min-h-[60px] w-2/4"
                    />
                    {alertmessage && (
                        <span className="text-red-500 text-sm mt-1">{alertmessage}</span>
                    )}
                </div>

                {/* Final rule display */}
                <div className="border bg-white border-gray-200 rounded-lg p-2">
                    <Label className="font-medium text-gray-700">Rules with values</Label>
                    <Textarea
                        value={finalRule}
                        className="w-full bg-gray-100 text-gray-800 disabled:opacity-100 mt-1"
                        disabled
                        rows={5}
                    />
                </div>

                {/* Action buttons */}
                <div className="flex justify-center items-center gap-4 pt-4">
                    {ruleObjValue?.Version && (
                        <Button
                            variant="outline"
                            onClick={handleUpdate}
                            disabled={isEditLoading}
                        >
                            {isEditLoading ? (
                                <><Loader2 className="h-4 w-4 animate-spin mr-2" />Updating...</>
                            ) : (
                                "Update Current Version"
                            )}
                        </Button>
                    )}
                    <Button
                        onClick={handleSaveNewVersion}
                        disabled={isCreateLoading}
                    >
                        {isCreateLoading ? (
                            <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
                        ) : (
                            "Save New Version"
                        )}
                    </Button>
                </div>

                {/* Alert */}
                <AlertDialog
                    open={alertOpen}
                    onOpenChange={handleAlertClose}
                    message={alertMsg}
                    status={alertStatus}
                />

                {/* Expression builder */}
                <ExpressionBuilderModal
                    open={openExpression}
                    onClose={() => { setOpenExpression(false); setExpressionData(null); }}
                    initialExpression={expressionData?.expressionValue || ""}
                    initialExpressionJson={
                        expressionData?.ExpressionJson
                            ? JSON.parse(expressionData.ExpressionJson)
                            : null
                    }
                    onSave={({ finalExpression, expressionJson }) => {
                        if (expressionData) {
                            const currentRows = methods.getValues("pricingRows");
                            const updatedRows = currentRows.map((row) =>
                                row.id === expressionData.id
                                    ? {
                                        ...row,
                                        expressionValue: finalExpression,
                                        ExpressionJson: JSON.stringify(expressionJson),
                                    }
                                    : row
                            );
                            setValue("pricingRows", updatedRows);
                        }
                        setOpenExpression(false);
                        setExpressionData(null);
                    }}
                />
            </div>
        </FormProvider>
    );
}